"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.StudioSharedService = exports.StudioReadableAspectRatio = void 0;
var StudioReadableAspectRatio;
(function (StudioReadableAspectRatio) {
    StudioReadableAspectRatio["9:16"] = "9:16";
    StudioReadableAspectRatio["4:5"] = "4:5";
    StudioReadableAspectRatio["2:3"] = "2:3";
    StudioReadableAspectRatio["1:1"] = "1:1";
    StudioReadableAspectRatio["16:9"] = "16:9";
    StudioReadableAspectRatio["1.91:1"] = "1.91:1";
})(StudioReadableAspectRatio || (exports.StudioReadableAspectRatio = StudioReadableAspectRatio = {}));
class StudioSharedService {
    /**
     * Formats a number to a specified number of decimal places, avoiding floating point errors.
     */
    static formatNumber(value, decimals = 2) {
        // As there are so many issues with floating point errors in JavaScript, we need to be careful with how we format the time
        // After running into many problems I considered just cutting out the number from a string and then placing it back together
        const absValue = Math.abs(value);
        const digitString = absValue.toString().split('.')[0];
        const parsedDigits = parseInt(digitString);
        const decimalString = absValue.toString().split('.')[1];
        let decimalStr = '';
        Array.from({ length: decimals }).forEach((_, i) => {
            const rawDecimalStr = decimalString?.slice(i, i + 1) ?? '0';
            const decimal = parseInt(rawDecimalStr);
            if (typeof decimal !== 'number' || isNaN(decimal)) {
                decimalStr += '0';
            }
            else {
                decimalStr += decimal.toString();
            }
        });
        const resultString = `${value < 0 ? '-' : ''}${parsedDigits}.${decimalStr}`;
        return parseFloat(resultString);
    }
    /**
     * Formats a time value in seconds to 2 decimal places, avoiding floating point errors.
     */
    static formatTime(rawSeconds) {
        // As there are so many issues with floating point errors in JavaScript, we need to be careful with how we format the time
        // After running into many problems I considered just cutting out the number from a string and then placing it back together
        const absSeconds = Math.abs(rawSeconds);
        const digitString = absSeconds.toString().split('.')[0];
        const parsedDigits = parseInt(digitString);
        const decimalString = absSeconds.toString().split('.')[1];
        const firstDecimalString = decimalString?.slice(0, 1) ?? '0';
        const parsedFirstDecimal = parseInt(firstDecimalString);
        const secondDecimalString = decimalString?.slice(1, 2) ?? '0';
        const parsedSecondDecimal = parseInt(secondDecimalString);
        const resultString = `${rawSeconds < 0 ? '-' : ''}${parsedDigits}.${parsedFirstDecimal}${parsedSecondDecimal}`;
        return parseFloat(resultString);
    }
    /**
     * Determines the closest matching aspect ratio format from given width/height dimensions.
     */
    static getExportFormatAspectRatioFromDimensions(projectDimensions) {
        const aspectRatio = projectDimensions.width / projectDimensions.height;
        const exportFormats = Object.values(StudioReadableAspectRatio);
        return Object.values(StudioReadableAspectRatio).reduce((acc, format) => {
            const formatAspectRatio = this.getExportFormatRatio(format);
            const formatDiff = Math.abs(aspectRatio - formatAspectRatio);
            const accAspectRatio = this.getExportFormatRatio(acc);
            const accDiff = Math.abs(aspectRatio - accAspectRatio);
            return accDiff < formatDiff ? acc : format;
        }, exportFormats[0]);
    }
    /**
     * Calculates the position and size for a file to fit or fill a canvas based on aspect ratio difference.
     */
    static calcFilePositionAndSize(fileDimensions, canvasDimensions) {
        if (!fileDimensions.width || !fileDimensions.height)
            return { x: 0, y: 0, width: 0, height: 0 };
        const fileWidth = fileDimensions.width;
        const fileHeight = fileDimensions.height;
        const fileAspectRatio = fileWidth / fileHeight;
        const canvasAspectRatio = canvasDimensions.width / canvasDimensions.height;
        const aspectRatioDifference = Math.abs(fileAspectRatio - canvasAspectRatio);
        const fillWidthScaleFactor = fileWidth ? canvasDimensions.width / fileWidth : 0;
        const fillHeightScaleFactor = fileHeight ? canvasDimensions.height / fileHeight : 0;
        const scaleFactor = aspectRatioDifference > 0.2
            ? // Fit to the closest side
                Math.min(fillWidthScaleFactor, fillHeightScaleFactor)
            : // Fill the entire canvas
                Math.max(fillWidthScaleFactor, fillHeightScaleFactor);
        const width = fileWidth * scaleFactor;
        const height = fileHeight * scaleFactor;
        return {
            width,
            height,
            x: -width / 2 + canvasDimensions.width / 2,
            y: -height / 2 + canvasDimensions.height / 2,
        };
    }
}
exports.StudioSharedService = StudioSharedService;
/**
 * Converts a readable aspect ratio string (e.g. '9:16') to a numeric ratio (width / height).
 */
StudioSharedService.getExportFormatRatio = (format) => {
    const widthRatio = Number(format.split(':')[0]);
    const heightRatio = Number(format.split(':')[1]);
    if (!heightRatio || !widthRatio || Number.isNaN(heightRatio) || Number.isNaN(widthRatio)) {
        return 9 / 16;
    }
    return widthRatio / heightRatio;
};
